import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import axios from "axios";
import { API } from "../App";

export default function WorkerVoicePage({ user }) {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [feedbackList, setFeedbackList] = useState([]);
  const [newTopic, setNewTopic] = useState("");
  const [newTopicDesc, setNewTopicDesc] = useState("");
  const [feedbackBody, setFeedbackBody] = useState("");
  const [feedbackCategory, setFeedbackCategory] = useState("general");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadTopics();
    loadFeedback();
  }, []);

  async function loadTopics() {
    try {
      const res = await axios.get(`${API}/worker-voice/topics`);
      setTopics(res.data.topics || []);
    } catch (err) {
      console.warn("WorkerVoice: loadTopics failed", err);
    }
  }

  async function loadFeedback() {
    try {
      const res = await axios.get(`${API}/worker-voice/feedback`);
      setFeedbackList(res.data.feedback || []);
    } catch (err) {
      console.warn("WorkerVoice: loadFeedback failed", err);
    }
  }

  async function handleSubmitTopic(e) {
    e.preventDefault();
    if (!newTopic.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/worker-voice/topics`, {
        topic: newTopic,
        description: newTopicDesc,
      });
      setNewTopic("");
      setNewTopicDesc("");
      loadTopics();
    } catch (err) {
      console.warn("WorkerVoice: submitTopic failed", err);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitFeedback(e) {
    e.preventDefault();
    if (!feedbackBody.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/worker-voice/feedback`, {
        category: feedbackCategory,
        title: feedbackCategory,
        body: feedbackBody,
      });
      setFeedbackBody("");
      loadFeedback();
    } catch (err) {
      console.warn("WorkerVoice: submitFeedback failed", err);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVote(topicId, vote) {
    try {
      await axios.post(`${API}/worker-voice/vote`, {
        target_type: "topic",
        target_id: topicId,
        vote,
      });
      loadTopics();
    } catch (err) {
      console.warn("WorkerVoice: vote failed", err);
    }
  }

  return (
    <MainLayout
      title="Worker Voice"
      subtitle="Feedback, trends, and worker concerns in one place."
      user={user}
      actions={
        <button onClick={() => navigate("/")} className="btn-secondary-dark" data-testid="back-home-btn">
          &larr; Back to Home
        </button>
      }
    >
      {/* Stats row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="worker-voice-stats">
        <div className="card-dark p-6">
          <p className="text-sm text-gray-400">Topics submitted</p>
          <h3 className="text-2xl font-bold mt-2" data-testid="topics-count">{topics.length}</h3>
        </div>
        <div className="card-dark p-6">
          <p className="text-sm text-gray-400">Feedback entries</p>
          <h3 className="text-2xl font-bold mt-2" data-testid="feedback-count">{feedbackList.length}</h3>
        </div>
        <div className="card-dark p-6">
          <p className="text-sm text-gray-400">Top concern</p>
          <h3 className="text-2xl font-bold mt-2" data-testid="top-concern">
            {topics.length > 0 ? topics[0]?.topic || "None" : "None yet"}
          </h3>
        </div>
      </div>

      {/* Submit forms */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Topic form */}
        <div className="card-dark p-6" data-testid="submit-topic-form">
          <h3 className="text-lg font-semibold mb-4">Suggest a Topic</h3>
          <form onSubmit={handleSubmitTopic} className="space-y-3">
            <input
              className="input-dark"
              placeholder="Topic name..."
              value={newTopic}
              onChange={(e) => setNewTopic(e.target.value)}
              data-testid="topic-input"
            />
            <textarea
              className="textarea-dark min-h-[80px]"
              placeholder="Describe why this matters..."
              value={newTopicDesc}
              onChange={(e) => setNewTopicDesc(e.target.value)}
              data-testid="topic-desc-input"
            />
            <button type="submit" className="btn-primary-dark" disabled={submitting} data-testid="submit-topic-btn">
              {submitting ? "Submitting..." : "Submit Topic"}
            </button>
          </form>
        </div>

        {/* Feedback form */}
        <div className="card-dark p-6" data-testid="submit-feedback-form">
          <h3 className="text-lg font-semibold mb-4">Share Feedback</h3>
          <form onSubmit={handleSubmitFeedback} className="space-y-3">
            <select
              className="select-dark"
              value={feedbackCategory}
              onChange={(e) => setFeedbackCategory(e.target.value)}
              data-testid="feedback-category-select"
            >
              <option value="general">General</option>
              <option value="safety">Safety</option>
              <option value="contract">Contract</option>
              <option value="feature_request">Feature Request</option>
              <option value="missing_content">Missing Content</option>
            </select>
            <textarea
              className="textarea-dark min-h-[80px]"
              placeholder="What's on your mind..."
              value={feedbackBody}
              onChange={(e) => setFeedbackBody(e.target.value)}
              data-testid="feedback-body-input"
            />
            <button type="submit" className="btn-primary-dark" disabled={submitting} data-testid="submit-feedback-btn">
              {submitting ? "Submitting..." : "Submit Feedback"}
            </button>
          </form>
        </div>
      </div>

      {/* Topics list with voting */}
      <div className="card-dark p-6 mt-6" data-testid="topics-list">
        <h3 className="text-lg font-semibold mb-4">Community Topics</h3>
        <div className="space-y-3">
          {topics.length > 0 ? (
            topics.map((t) => (
              <div
                key={t.topic_id || t.topic}
                className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-950 p-4"
                data-testid={`topic-item-${t.topic_id}`}
              >
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white">{t.topic}</p>
                  {t.description && <p className="text-xs text-gray-400 mt-1">{t.description}</p>}
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleVote(t.topic_id, 1)}
                    className="px-2 py-1 rounded-lg border border-zinc-700 text-cyan-400 text-xs hover:bg-cyan-500/10 transition"
                    data-testid={`upvote-${t.topic_id}`}
                  >
                    +1
                  </button>
                  <span className="text-sm font-bold text-white min-w-[24px] text-center">{t.score || 0}</span>
                  <button
                    onClick={() => handleVote(t.topic_id, -1)}
                    className="px-2 py-1 rounded-lg border border-zinc-700 text-red-400 text-xs hover:bg-red-500/10 transition"
                    data-testid={`downvote-${t.topic_id}`}
                  >
                    -1
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No topics yet. Be the first to suggest one.</p>
          )}
        </div>
      </div>

      {/* Recent feedback */}
      <div className="card-dark p-6 mt-6" data-testid="feedback-list">
        <h3 className="text-lg font-semibold mb-4">Recent Worker Feedback</h3>
        <div className="space-y-3">
          {feedbackList.length > 0 ? (
            feedbackList.slice(0, 10).map((f, i) => (
              <div key={f.feedback_id || i} className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="badge-accent text-xs">{f.category || "general"}</span>
                </div>
                <p className="text-sm text-gray-300">{f.body}</p>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No feedback yet.</p>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
